<template>
  <div
    class="flex flex-col min-w-[200px] max-w-[200px] bg-[#FAFAFA] p-2.5 border-r border-[#E5E5E5] relative z-101"
  >
    <RouterLink
      v-for="(item, index) in items"
      :key="index"
      :to="item.to"
      class="flex items-center opacity-60 w-full h-8 hover:bg-gray-200/20 rounded-md text-gray-800 px-2.5 mb-0.5 last:mb-0"
      active-class="opacity-100! bg-gray-200/20"
    >
      <Icon
        :size="18"
        :name="item.icon as IconName"
        class="mr-2.5 stroke-gray-800"
      />
      <div class="font-secondary text-[14px] leading-[20px]">
        {{ item.label }}
      </div>
    </RouterLink>

    <div
      v-if="showCallout"
      class="mt-auto p-2.5 pt-[7px] border rounded-md"
      :class="calloutClasses"
    >
      <div
        class="font-secondary font-medium text-[13px] leading-[18px] text-gray-500 mb-2"
      >
        <template v-if="noFilesAdded">
          Get more from your copyrights. Go Pro
        </template>
        <template v-if="!noFilesAdded">
          <div class="font-semibold text-gray-800">
            <template v-if="fileLimitReached"> File limit reached </template>
            <template v-else>1 of 2 files added</template>
          </div>
          <div>
            Upgrade to add <template v-if="!isBundlePlan">unlimited</template>
            <template v-else>more</template> files
          </div>
        </template>
      </div>
      <UiButton
        class="w-full"
        :primary="!fileLimitReached"
        :danger="fileLimitReached"
        @click="handleUpgradeClick"
      >
        Upgrade
      </UiButton>
    </div>

    <div
      v-else-if="billingStore.billing?.upsell"
      class="bg-green-200/25 mt-auto p-2.5 border rounded-md border-green-700/25"
    >
      <Pill class="mb-1.5" success>🎉 Limited time </Pill>
      <div
        class="font-secondary font-medium text-[13px] leading-[18px] text-gray-500 mb-2"
      >
        {{ format(billingStore.billing?.upsell?.text) }}
      </div>
      <UiButton class="w-full" primary @click="handleClick">
        {{ billingStore.billing?.upsell?.ctaText }}
      </UiButton>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useUserStore } from '@/shared/stores/user'
import { Icon, Pill, UiButton, type IconName } from '@dacr/ui'
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import { useDialog } from '@/shared/dialogs/useDialog'
import { Dialogs } from '@/shared/dialogs/types'
import { useBillingStore } from '@/features/billing/store'
import {
  PlanType,
  usePlansQueryQuery,
  UserPermission,
} from '@/shared/graphql/legacy/generated'
import { SelectPlanType } from '@/shared/types/index'
import { analytics } from '@/shared/services/analytics'
import { useFeatureFlag } from '@/shared/composables/useFeatureFlag'
import { FeatureFlag } from '@/shared/stores/featureFlags'

interface Item {
  label: string
  icon: string
  to: string
  children?: Omit<Item, 'children' | 'icon'>[]
}

const { result } = usePlansQueryQuery()

const router = useRouter()
const userStore = useUserStore()
const billingStore = useBillingStore()
const dialog = useDialog()
const aiEnabled = useFeatureFlag(FeatureFlag.AI)

const items = computed<Item[]>(() => [
  {
    label: 'Home',
    icon: 'Home',
    to: '/home',
  },
  {
    label: 'Archived',
    icon: 'Archive',
    to: '/archive',
  },
  ...(aiEnabled.value
    ? [
        {
          label: 'Infringement',
          icon: 'Alert',
          to: '/infringement',
        },
      ]
    : []),
])

const isBasic = computed(
  () => !userStore.user?.permissions.includes(UserPermission.Pro)
)

const isBundlePlan = computed(
  () => billingStore.billing?.plan.type === PlanType.Bundle
)

const noFilesAdded = computed(() => !userStore.user?.totalFiles)

const fileLimitReached = computed(
  () => userStore.user?.totalFiles === userStore.user?.totalFilesLimit
)

const showCallout = computed(
  () =>
    userStore.user &&
    (isBasic.value || (isBundlePlan.value && fileLimitReached.value))
)

const calloutClasses = computed(() => ({
  'border-red-700/25 bg-red-200/50': fileLimitReached.value,
  'border-green-700/25 bg-green-200/50': !fileLimitReached.value,
}))

const handleUpgradeClick = () => {
  if (fileLimitReached.value && isBasic.value) {
    return dialog.open(Dialogs.UpgradeToPro, {
      props: {
        hasReachedLimit: true,
      },
    })
  }

  if (fileLimitReached.value && isBundlePlan.value) {
    return dialog.open(Dialogs.UpgradeBundleLimit, {
      props: {
        hasReachedLimit: true,
      },
    })
  }

  dialog.open(Dialogs.UpgradeToPro)
}

const format = (text?: string) => {
  if (!text) return

  return text.replace(
    '<price>',
    billingStore.billing?.upsell?.planItem.prices?.[0]?.details
      ?.renderedPrice || ''
  )
}

const handleClick = () => {
  if (
    !billingStore.billing?.upsell?.plan ||
    !billingStore.billing.upsell.planItem
  )
    return

  analytics.track('upsell_cta_clicked', {
    amount:
      (billingStore.billing.upsell.planItem.prices?.[0]?.details?.amount || 0) /
      100,
    currency:
      billingStore.billing.upsell.planItem.prices?.[0]?.details?.currency || '',
  })

  const billingCycle =
    billingStore.billing.upsell.planItem.prices[0].details?.billingCycleType ||
    'month'

  billingStore.setPlanData({
    plan: billingStore.billing?.upsell?.plan,
    planItem: billingStore.billing?.upsell?.planItem,
    billingCycle: {
      type: billingCycle,
      description:
        result.value?.plans?.groupByBillingCycle.find(
          (cycle) => cycle?.billingCycleType === billingCycle
        )?.billingCycleDescription || '',
    },
  })

  router.push(`/${SelectPlanType.Change}/checkout`)
}
</script>
